<template>
  <v-row no-gutters class="pt-5 pr-2">
    <v-col v-if="isFetched">
      <card-title-row translation-path="sidebar.dmaZones" />
      <template v-if="hasHistoricWaterUsage">
        <card-title-row is-subtitle translation-path="sidebar.waterDissolution" class="pt-5" />
        <v-form v-model="isFormValid">
          <sidebar-row>
            <v-row no-gutters class="pb-2" align="baseline">
              <v-col cols="4" class="text-left checkbox-label">
                {{ $i18n.t('sidebar.waterConsumptionPeriod') }}:
              </v-col>
              <v-col cols="1">
                <v-icon class="pr-1" color="rgba(0,0,0,0.7)">mdi-calendar</v-icon>
              </v-col>
              <v-col cols="7">
                <data-input v-model="dateRangeValue" v-bind="dateRangeInput" />
              </v-col>
            </v-row>
          </sidebar-row>
        </v-form>
        <sidebar-row>
          <v-checkbox
            class="py-0 my-0 black-label"
            color="primary"
            v-model="isChoroplethMap"
            @change="toggleChoroplethMap"
            :disabled="isChoroplethMapLoading"
          >
            <template #label>
              <v-row no-gutters class="checkbox-label">
                {{ checkboxLabel }}
              </v-row>
            </template>
            <template #append>
              <main-loader v-if="isChoroplethMapLoading" size="25" />
            </template>
          </v-checkbox>
        </sidebar-row>
        <sidebar-row border-bottom v-if="rangesText.length > 0 && isChoroplethMap">
          <v-col cols="auto" class="px-0 pt-0">
            <v-row no-gutters class="text-body-2">
              <div class="text-left text-body-2 font-weight-medium font--text pb-4">
                {{ $i18n.t('sidebar.legend') }}
              </div>
            </v-row>
            <v-row no-gutters class="align-center" v-for="(range, idx) in [...rangesText].reverse()" :key="idx">
              <v-col cols="auto" class="pr-1">
                <legend-layer-list-icon :color="range.color" layer-type="polygon"></legend-layer-list-icon>
              </v-col>
              <v-col cols="auto" class="text-body-2">
                {{ range.text }}
              </v-col>
            </v-row>
          </v-col>
        </sidebar-row>
      </template>
      <card-title-row is-subtitle translation-path="sidebar.statisticsForZone" class="pt-5" />
      <v-row no-gutters class="mr-2 mt-4">
        <template v-for="input of inputs">
          <sidebar-form-row :key="input.name" v-bind="input">
            <template slot="zone">
              <sidebar-geometry-input v-model="zone" v-bind="input" ref="geometry" />
            </template>
          </sidebar-form-row>
        </template>
      </v-row>
      <sidebar-row>
        <v-row no-gutters class="pb-4">
          <v-col class="text-left geometry-input-value">
            {{ $i18n.t('default.or').toLowerCase() }}
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row :border-bottom="isDmaZoneStatisticsCalculated">
        <v-row no-gutters class="pb-4">
          <v-col cols="auto" class="pa-0">
            <v-tooltip bottom color="font">
              <template #activator="{ on }">
                <div v-on="on">
                  <base-button-subheader
                    :disabled="!isFormValid && !hasHistoricWaterUsage"
                    :translation-path="'button.countStatisticsForDmaZones'"
                    @click="calculateDmaZonesStatistics()"
                    :loading="isDmaZonesStatisticsLoading"
                  />
                </div>
              </template>
              {{ $i18n.t('button.countStatisticsForDmaZones') }}
            </v-tooltip>
          </v-col>
        </v-row>
      </sidebar-row>

      <template v-if="isDmaZoneStatisticsCalculated">
        <card-title-row is-subtitle translation-path="sidebar.statisticsForDrawedZone" class="pt-5" />
        <sidebar-row v-if="dmaZoneStatisticsRangeObject">
          <italic-subheader
            :straightText="$i18n.t('sidebar.statisticsForDrawedZoneDateRange', dmaZoneStatisticsRangeObject)"
          ></italic-subheader>
        </sidebar-row>
        <sidebar-row>
          <sidebar-summary :items="zoneStatisticsItems" class="my-4">
            <template v-for="item of zoneStatisticsItems" #[item.name]="">
              {{ partition[item.name] }}
            </template>
          </sidebar-summary>
        </sidebar-row>
      </template>
      <dma-zones-statistics-modal
        :is-dma-zones-statistics-visible.sync="isDmaZonesStatisticsCalculated"
        :dma-zones-statistics-range-object="dmaZoneStatisticsRangeObject"
        :zone-statistics-items="zoneStatisticsItems"
      />
    </v-col>
    <main-loader v-else />
  </v-row>
</template>
<script>
import { debounce } from 'lodash';
import { call, get } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import MainLoader from '@/components/MainLoader';
import SidebarFormRow from '@/components/SidebarFormRow';
import SidebarGeometryInput from '@/components/SidebarGeometryInput';
import SidebarSummary from '@/components/SidebarSummary';
import SidebarRow from '@/components/SidebarRow';
import LegendLayerListIcon from '@/components/LegendLayerListIcon';

export default {
  name: 'WaterDissolutionDma',
  components: {
    SidebarRow,
    CardTitleRow,
    DataInput,
    DmaZonesStatisticsModal: () => import('@/components/DmaZonesStatisticsModal'),
    ItalicSubheader,
    MainLoader,
    SidebarFormRow,
    SidebarGeometryInput,
    SidebarSummary,
    LegendLayerListIcon,
  },
  data: () => ({
    dateRangeValue: [],
    isChoroplethMap: false,
    isChoroplethMapLoading: false,
    isDmaZoneStatisticsCalculated: false,
    isDmaZonesStatisticsCalculated: false,
    isDmaZoneStatisticsLoading: false,
    isDmaZonesStatisticsLoading: false,
    isFetched: false,
    isFormValid: false,
    localeDateFormat: 'MMMM YYYY',
    rangesText: [],
    zone: undefined,
  }),
  computed: {
    billingsLegalizationSoonThreshold: get('admin/settingsValues@billings_module_legalization_soon_threshold'),
    hasHistoricWaterUsage: get('admin/modulesMapping@billing.has_historic_water_usage_data'),
    partition: get('billings/partition'),
    waterUsageDateRange: get('billings/waterUsageDateRange'),
    checkboxLabel() {
      const m3 = new DOMParser().parseFromString('m&sup3', 'text/html').body.textContent;
      return `${this.$i18n.t('sidebar.averageDailyWaterConsumptionCartogram')} (${m3})`;
    },
    dateRangeInput() {
      return {
        dataType: { name: 'daterange' },
        datePickerProps: { ...this.waterUsageDateRange, localeDateFormat: this.localeDateFormat },
      };
    },
    dmaZoneStatisticsRangeObject() {
      if (this.dateRangeValue && this.dateRangeValue.length > 1) {
        return {
          start: this.$dayjs(this.dateRangeValue[0]).format(this.localeDateFormat),
          end: this.$dayjs(this.dateRangeValue[1]).format(this.localeDateFormat),
        };
      }
      if (this.waterUsageDateRange) {
        return {
          start: this.$dayjs(this.waterUsageDateRange.min).format(this.localeDateFormat),
          end: this.$dayjs(this.waterUsageDateRange.max).format(this.localeDateFormat),
        };
      }
      return null;
    },
    zoneStatisticsItems() {
      return [
        {
          name: 'watermeters_count',
          straightText: this.$i18n.t('sidebar.watermetersCount'),
          color: 'rgba(0,145,234)',
        },
        {
          name: 'before_due',
          straightText: this.$i18n.t('sidebar.legalizationBeforeDue'),
          color: 'rgba(0,145,234)',
        },
        {
          name: 'past_due',
          straightText: this.$i18n.t('sidebar.legalizationPastDue'),
          color: 'rgba(0,145,234)',
        },
        {
          name: 'soon_past_due',
          straightText: this.$i18n.t('sidebar.legalizationSoonPastDue', {
            days: this.billingsLegalizationSoonThreshold,
          }),
          color: 'rgba(0,145,234)',
        },
        {
          name: 'no_data',
          straightText: this.$i18n.t('sidebar.legalizationNoData'),
          color: 'rgba(0,145,234)',
        },
        {
          name: 'day_average',
          straightText: this.$i18n.t('sidebar.legalizationDayAverage'),
          color: 'rgba(0,145,234)',
        },
        {
          name: 'sum',
          straightText: this.$i18n.t('sidebar.legalizationSum'),
          color: 'rgba(0,145,234)',
        },
        {
          name: 'current_year',
          straightText: this.$i18n.t('sidebar.legalizationCurrentYear'),
          color: 'rgba(0,145,234)',
        },
        {
          name: 'next_year',
          straightText: this.$i18n.t('sidebar.legalizationNextYear'),
          color: 'rgba(0,145,234)',
        },
      ];
    },
    inputs() {
      return [
        {
          name: 'zone',
          icon: 'mdi-map-marker',
          iconSize: '22px',
          hasNameColumn: false,
          oneLineInput: false,
          cols: '11',
          hint: this.$i18n.t('sidebar.drawStatisticsZoneOnMap'),
          geometryType: 'Polygon',
          addedGeometryText: 'sidebar.zoomToZone',
          drawGeometryText: 'sidebar.drawZone',
          customButtonAction: {
            action: this.toggleDmaZoneStatistics,
            loading: this.isDmaZoneStatisticsLoading,
            text: this.isDmaZoneStatisticsCalculated ? 'button.reset' : 'button.calculate',
            disabled: !this.isFormValid && !this.isDmaZoneStatisticsCalculated && !this.hasHistoricWaterUsage,
          },
        },
      ];
    },
  },
  watch: {
    dateRangeValue(nV, oV) {
      if (JSON.stringify(nV) !== JSON.stringify(oV)) {
        this.updateChoroplethMap();
      }
    },
  },
  methods: {
    getPartition: call('billings/getPartition'),
    getPartitionByDma: call('billings/getPartitionByDma'),
    getWaterUsageDateRange: call('billings/getWaterUsageDateRange'),
    async calculateDmaZoneStatistics() {
      try {
        this.isDmaZoneStatisticsLoading = true;
        const payload = {
          zone: this.zone,
        };
        if (this.dateRangeValue && this.dateRangeValue.length > 1) {
          payload.water_usage_since = this.dateRangeValue[0] + '-01';
          payload.water_usage_until = this.dateRangeValue[1] + `-${this.getLastDay(this.dateRangeValue[1])}`;
        }
        await this.getPartition(payload);
        this.isDmaZoneStatisticsLoading = false;
        this.isDmaZoneStatisticsCalculated = true;
      } catch {
        this.isDmaZoneStatisticsLoading = false;
      }
    },
    async calculateDmaZonesStatistics() {
      try {
        this.isDmaZonesStatisticsLoading = true;
        const params = {};
        if (this.dateRangeValue && this.dateRangeValue.length > 1) {
          params.water_usage_since = this.dateRangeValue[0] + '-01';
          params.water_usage_until = this.dateRangeValue[1] + `-${this.getLastDay(this.dateRangeValue[1])}`;
        }
        await this.getPartitionByDma(params);
        this.isDmaZonesStatisticsLoading = false;
        this.isDmaZonesStatisticsCalculated = true;
      } catch {
        this.isDmaZonesStatisticsLoading = false;
      }
    },
    async toggleChoroplethMap(nV) {
      if (nV) {
        this.isChoroplethMapLoading = true;
        const params = {};
        if (this.dateRangeValue && this.dateRangeValue.length > 1) {
          params.water_usage_since = this.dateRangeValue[0] + '-01';
          params.water_usage_until = this.dateRangeValue[1] + `-${this.getLastDay(this.dateRangeValue[1])}`;
        }
        await this.getPartitionByDma(params);
        this.isChoroplethMapLoading = false;
      } else {
        this.rangesText = [];
      }
      this.$root.$emit('toggleDmaZonesChoroplethMap', nV);
    },
    getLastDay(date) {
      return this.$dayjs(date).daysInMonth();
    },
    toggleDmaZoneStatistics() {
      if (this.isDmaZoneStatisticsCalculated) {
        this.isDmaZoneStatisticsCalculated = false;
        this.$refs['geometry'][0].deleteGeometry();
      } else {
        this.calculateDmaZoneStatistics();
      }
    },
    updateDmaZonesChoroplethMapLegend(data) {
      const rangesLength = data.rangesColors.length;
      const rangesText = [];
      data.rangesColors.forEach((color, idx) => {
        let rangeText = '';
        if (idx == '0') {
          rangeText = `0 - ${data.ranges[idx]}`;
        } else if (idx == rangesLength - 1) {
          rangeText = `> ${data.ranges[idx - 1]}`;
        } else {
          rangeText = `${data.ranges[idx - 1]} - ${data.ranges[idx]}`;
        }
        rangesText.push({ color, text: rangeText });
      });
      this.rangesText = rangesText;
    },
    async updateChoroplethMap() {
      if (this.isChoroplethMap && this.isFormValid) {
        this.toggleChoroplethMap(true);
      }
    },
    async init() {
      if (!this.hasHistoricWaterUsage) {
        this.isFetched = true;
        return;
      }
      await this.getWaterUsageDateRange();
      this.dateRangeValue = [
        this.waterUsageDateRange.min.substring(0, 7),
        this.waterUsageDateRange.max.substring(0, 7),
      ];
      this.isFetched = true;
    },
  },
  mounted() {
    this.toggleChoroplethMap(false);
    this.init();
  },
  created() {
    this.$root.$off('updateDmaZonesChoroplethMapLegend');
    this.$root.$on('updateDmaZonesChoroplethMapLegend', this.updateDmaZonesChoroplethMapLegend);
    this.updateChoroplethMap = debounce(this.updateChoroplethMap, 1000);
  },
};
</script>
<style lang="scss" scoped>
.divider-row {
  @include border(bottom);
}
.checkbox-label {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.87);
}
.geometry-input-value {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
}
</style>
